import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { profileService, EmployerProfile as EmployerProfileType } from '../../services/profileService';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  BriefcaseIcon,
  ChartBarIcon,
  PencilIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  UsersIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon,
  EyeIcon,
  PhotoIcon,
  XMarkIcon,
  PlusIcon
} from '@heroicons/react/24/outline';



const EmployerProfile: React.FC = () => {
  const { userProfile } = useAuth();
  const [profile, setProfile] = useState<EmployerProfileType | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newBenefit, setNewBenefit] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newDepartment, setNewDepartment] = useState('');

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const profileData = await profileService.getCurrentProfile() as EmployerProfileType;
      const formattedProfile = profileService.formatProfileForDisplay(profileData);
      
      // Ensure displayName has a value, prioritizing full name from user data
      if (userProfile && (!formattedProfile.displayName || formattedProfile.displayName.trim() === '' || formattedProfile.displayName === formattedProfile.companyName + ' Representative')) {
        const fullName = `${userProfile.firstName} ${userProfile.lastName}`.trim();
        if (fullName && fullName !== ' ') {
          formattedProfile.displayName = fullName;
        } else if (!formattedProfile.displayName || formattedProfile.displayName.trim() === '') {
          formattedProfile.displayName = formattedProfile.companyName + ' Representative';
        }
      }
      
      setProfile(formattedProfile);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;

    try {
      setLoading(true);
      
      // Ensure displayName is properly set before saving
      const profileToSave = { ...profile };
      if (userProfile && (!profileToSave.displayName || profileToSave.displayName.trim() === '' || profileToSave.displayName === profileToSave.companyName + ' Representative')) {
        const fullName = `${userProfile.firstName} ${userProfile.lastName}`.trim();
        if (fullName && fullName !== ' ') {
          profileToSave.displayName = fullName;
        } else if (!profileToSave.displayName || profileToSave.displayName.trim() === '') {
          profileToSave.displayName = profileToSave.companyName + ' Representative';
        }
      }
      
      // Validate profile data
      const errors = profileService.validateEmployerProfile(profileToSave);
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }

      const updatedProfile = await profileService.updateProfile(profileToSave) as EmployerProfileType;
      const formattedProfile = profileService.formatProfileForDisplay(updatedProfile);
      
      // Ensure displayName is still properly set after update
      if (userProfile && (!formattedProfile.displayName || formattedProfile.displayName.trim() === '' || formattedProfile.displayName === formattedProfile.companyName + ' Representative')) {
        const fullName = `${userProfile.firstName} ${userProfile.lastName}`.trim();
        if (fullName && fullName !== ' ') {
          formattedProfile.displayName = fullName;
        } else if (!formattedProfile.displayName || formattedProfile.displayName.trim() === '') {
          formattedProfile.displayName = formattedProfile.companyName + ' Representative';
        }
      }
      
      setProfile(formattedProfile);
      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = (field: keyof EmployerProfileType, value: any) => {
    if (!profile) return;
    setProfile(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', 'company-logo');

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData
      });

      const data = await response.json();
      
      if (data.success) {
        updateProfile('companyLogo', data.data.url);
        toast.success('Logo uploaded successfully!');
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const addBenefit = () => {
    if (!newBenefit.trim() || !profile) return;
    const updatedBenefits = [...(profile.companyBenefits || []), newBenefit.trim()];
    updateProfile('companyBenefits', updatedBenefits);
    setNewBenefit('');
  };

  const removeBenefit = (index: number) => {
    if (!profile) return;
    const updatedBenefits = profile.companyBenefits.filter((_, i) => i !== index);
    updateProfile('companyBenefits', updatedBenefits);
  };

  const addValue = () => {
    if (!newValue.trim() || !profile) return;
    const updatedValues = [...(profile.companyValues || []), newValue.trim()];
    updateProfile('companyValues', updatedValues);
    setNewValue('');
  };

  const removeValue = (index: number) => {
    if (!profile) return;
    const updatedValues = profile.companyValues.filter((_, i) => i !== index);
    updateProfile('companyValues', updatedValues);
  };

  const addDepartment = () => {
    if (!newDepartment.trim() || !profile) return;
    const updatedDepartments = [...(profile.departments || []), newDepartment.trim()];
    updateProfile('departments', updatedDepartments);
    setNewDepartment('');
  };

  const removeDepartment = (index: number) => {
    if (!profile) return;
    const updatedDepartments = profile.departments.filter((_, i) => i !== index);
    updateProfile('departments', updatedDepartments);
  };


  const tabs = [
    { id: 'overview', name: 'Company Overview', icon: BuildingOfficeIcon },
    { id: 'hiring', name: 'Hiring Stats', icon: ChartBarIcon },
    { id: 'jobs', name: 'Job Postings', icon: BriefcaseIcon },
    { id: 'culture', name: 'Company Culture', icon: UserGroupIcon }
  ];

  const renderOverviewTab = () => {
    if (!profile) return null;
    
    return (
      <div className="space-y-6">
        {/* Company Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="flex flex-col items-center space-y-2">
                <div className="relative">
                  <div className="h-20 w-20 bg-indigo-100 rounded-lg flex items-center justify-center">
                    {profile.companyLogo ? (
                      <img src={profile.companyLogo} alt="" className="h-20 w-20 rounded-lg object-cover" />
                    ) : (
                      <BuildingOfficeIcon className="h-12 w-12 text-indigo-600" />
                    )}
                  </div>
                  {uploading && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center z-20">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                    </div>
                  )}
                </div>
                {isEditing && (
                  <label className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-md cursor-pointer shadow-sm transition-colors text-xs font-medium">
                    <PhotoIcon className="h-3 w-3 inline mr-1" />
                    Upload Logo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
              <div className="flex-1">
                {isEditing ? (
                  <div className="space-y-3 space-x-2">
                    <input
                      type="text"
                      value={profile.companyName}
                      onChange={(e) => updateProfile('companyName', e.target.value)}
                      className="text-2xl font-bold text-gray-900 border-b border-gray-300 focus:border-indigo-500 outline-none bg-transparent"
                      placeholder="Company Name"
                    />
                    <input
                      type="text"
                      value={profile.displayName}
                      onChange={(e) => updateProfile('displayName', e.target.value)}
                      className="text-lg font-medium text-gray-700 border-b border-gray-300 focus:border-indigo-500 outline-none bg-transparent"
                      placeholder="Your Name (Company Representative)"
                    />
                    <textarea
                      value={profile.description || ''}
                      onChange={(e) => updateProfile('description', e.target.value)}
                      className="text-gray-600 border border-gray-300 rounded px-3 py-2 w-full focus:border-indigo-500 outline-none resize-none"
                      placeholder="Company Description"
                      rows={2}
                    />
                  </div>
                ) : (
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{profile.companyName}</h1>
                    <p className="text-lg text-gray-700 font-medium">{profile.displayName}</p>
                    <p className="text-gray-600 mt-1">{profile.description}</p>
                  </div>
                )}
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  <div className="flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    {isEditing ? (
                      <input
                        type="text"
                        value={profile.location || ''}
                        onChange={(e) => updateProfile('location', e.target.value)}
                        className="border-b border-gray-300 focus:border-indigo-500 outline-none bg-transparent text-sm"
                        placeholder="Location"
                      />
                    ) : (
                      profile.location
                    )}
                  </div>
                  <div className="flex items-center">
                    <UsersIcon className="h-4 w-4 mr-1" />
                    {isEditing ? (
                      <select
                        value={profile.companySize}
                        onChange={(e) => updateProfile('companySize', e.target.value)}
                        className="border-b border-gray-300 focus:border-indigo-500 outline-none bg-transparent text-sm"
                      >
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="501-1000">501-1000 employees</option>
                        <option value="1000+">1000+ employees</option>
                      </select>
                    ) : (
                      profile.companySize
                    )}
                  </div>
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-4 w-4 mr-1" />
                    {isEditing ? (
                      <select
                        value={profile.industry}
                        onChange={(e) => updateProfile('industry', e.target.value)}
                        className="border-b border-gray-300 focus:border-indigo-500 outline-none bg-transparent text-sm"
                      >
                        <option value="Technology">Technology</option>
                        <option value="Finance">Finance</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Education">Education</option>
                        <option value="Retail">Retail</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Consulting">Consulting</option>
                        <option value="Other">Other</option>
                      </select>
                    ) : (
                      profile.industry
                    )}
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center px-4 py-2 text-indigo-600 hover:text-indigo-800"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              {isEditing ? 'Cancel Edit' : 'Edit Profile'}
            </button>
          </div>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <BriefcaseIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{profile.jobPostingStats.activeJobs}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Applications</p>
              <p className="text-2xl font-bold text-gray-900">{profile.jobPostingStats.totalApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <ArrowTrendingUpIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-gray-900">{profile.hiringStats.successRate}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <StarIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Candidate Rating</p>
              <p className="text-2xl font-bold text-gray-900">{profile.hiringStats.candidateRating}/5</p>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Performance */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">This Month's Performance</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <EyeIcon className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{profile.jobPostingStats.activeJobs * 125}</p>
            <p className="text-sm text-gray-600">Job Views</p>
          </div>
          <div className="text-center">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{Math.floor(profile.jobPostingStats.totalApplications * 1.2)}</p>
            <p className="text-sm text-gray-600">Profile Views</p>
          </div>
          <div className="text-center">
            <DocumentTextIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{Math.floor(profile.jobPostingStats.totalApplications * 0.3)}</p>
            <p className="text-sm text-gray-600">Applications</p>
          </div>
          <div className="text-center">
            <UserGroupIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{Math.floor(profile.jobPostingStats.totalApplications * 0.15)}</p>
            <p className="text-sm text-gray-600">Interviews</p>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center">
            <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
            {isEditing ? (
              <input
                type="email"
                value={profile.email}
                onChange={(e) => updateProfile('email', e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 focus:border-indigo-500 outline-none flex-1"
                placeholder="Email"
              />
            ) : (
              <span className="text-gray-900">{profile.email}</span>
            )}
          </div>
          <div className="flex items-center">
            <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
            {isEditing ? (
              <input
                type="tel"
                value={profile.phone || ''}
                onChange={(e) => updateProfile('phone', e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 focus:border-indigo-500 outline-none flex-1"
                placeholder="Phone Number"
              />
            ) : (
              <span className="text-gray-900">{profile.phone || 'Not provided'}</span>
            )}
          </div>
          <div className="flex items-center">
            <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3" />
            {isEditing ? (
              <input
                type="url"
                value={profile.website || ''}
                onChange={(e) => updateProfile('website', e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 focus:border-indigo-500 outline-none flex-1"
                placeholder="https://company-website.com"
              />
            ) : (
              <span className="text-gray-900">{profile.website || 'Not provided'}</span>
            )}
          </div>
          <div className="flex items-center">
            <MapPinIcon className="h-5 w-5 text-gray-400 mr-3" />
            {isEditing ? (
              <input
                type="text"
                value={profile.location || ''}
                onChange={(e) => updateProfile('location', e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 focus:border-indigo-500 outline-none flex-1"
                placeholder="Company Location"
              />
            ) : (
              <span className="text-gray-900">{profile.location || 'Not provided'}</span>
            )}
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderHiringTab = () => {
    if (!profile) return null;
    
    return (
      <div className="space-y-6">
        {/* Hiring Metrics */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Hiring Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{profile.hiringStats.averageTimeToHire}</p>
              <p className="text-sm text-gray-600">Days to Hire</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{profile.jobPostingStats.hiredCandidates}</p>
              <p className="text-sm text-gray-600">Successful Hires</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-600">{Math.round(profile.jobPostingStats.averageApplicationsPerJob)}</p>
              <p className="text-sm text-gray-600">Avg. Applications/Job</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-3xl font-bold text-yellow-600">{profile.hiringStats.repeatApplicants}</p>
              <p className="text-sm text-gray-600">Repeat Applicants</p>
            </div>
          </div>
        </div>

      {/* Recent Hires */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Hires</h2>
        <div className="space-y-4">
          <p className="text-gray-500 text-center py-8">No recent hires data available</p>
        </div>
      </div>
    </div>
    );
  };

  const renderCultureTab = () => {
    if (!profile) return null;
    
    return (
      <div className="space-y-6">
        {/* Company Values */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Values</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {profile.companyValues.map((value, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
              >
                {value}
                {isEditing && (
                  <button
                    onClick={() => removeValue(index)}
                    className="ml-2 text-indigo-600 hover:text-indigo-800"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
          {isEditing && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addValue()}
                className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none flex-1"
                placeholder="Add a company value"
              />
              <button
                onClick={addValue}
                className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Benefits */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Employee Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {profile.companyBenefits.map((benefit, index) => (
              <div key={index} className="flex items-center">
                <div className="h-2 w-2 bg-green-400 rounded-full mr-3"></div>
                <span className="text-gray-900 flex-1">{benefit}</span>
                {isEditing && (
                  <button
                    onClick={() => removeBenefit(index)}
                    className="ml-2 text-red-600 hover:text-red-800"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {isEditing && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newBenefit}
                onChange={(e) => setNewBenefit(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addBenefit()}
                className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none flex-1"
                placeholder="Add an employee benefit"
              />
              <button
                onClick={addBenefit}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Departments */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Departments</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            {profile.departments.map((dept, index) => (
              <div key={index} className="text-center p-4 bg-gray-50 rounded-lg relative">
                <UserGroupIcon className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">{dept}</p>
                {isEditing && (
                  <button
                    onClick={() => removeDepartment(index)}
                    className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {isEditing && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newDepartment}
                onChange={(e) => setNewDepartment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addDepartment()}
                className="border border-gray-300 rounded px-3 py-2 focus:border-indigo-500 outline-none flex-1"
                placeholder="Add a department"
              />
              <button
                onClick={addDepartment}
                className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'hiring':
        return renderHiringTab();
      case 'culture':
        return renderCultureTab();
      default:
        return renderOverviewTab();
    }
  };

  if (loading || !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subscription Banner */}
        {profile && (
          <div className={`mb-6 p-4 rounded-lg ${
            profile.subscriptionTier === 'enterprise' ? 'bg-purple-100 border border-purple-200' :
            profile.subscriptionTier === 'premium' ? 'bg-blue-100 border border-blue-200' :
            'bg-gray-100 border border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">
                  Current Plan: <span className="capitalize">{profile.subscriptionTier}</span>
                </p>
                <p className="text-sm text-gray-600">
                  {profile.subscriptionTier === 'basic' 
                    ? 'Upgrade to access advanced hiring features'
                    : 'You have access to premium hiring tools'
                  }
                </p>
              </div>
              {profile.subscriptionTier === 'basic' && (
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                  Upgrade Plan
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {renderTab()}

        {/* Save/Cancel buttons when editing */}
        {isEditing && (
          <div className="fixed bottom-6 right-6 flex gap-3">
            <button
              onClick={() => setIsEditing(false)}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveProfile}
              disabled={loading}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployerProfile;